import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axiosInstance from '../../config/axios';
import { toastService } from '../../services/toast';
import { fingerprintService } from '../../services/fingerprint';
import { deviceMatchingService } from '../../services/deviceMatchingService';

const Login = () => {
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  
  // التحقق من حالة تسجيل الدخول
  useEffect(() => {
    const checkLoginStatus = () => {
      // التحقق من وجود توكن
      const token = sessionStorage.getItem('token');
      const userType = sessionStorage.getItem('userType');
      const userData = sessionStorage.getItem('user');

      // إذا كان المستخدم مسجل دخول، نحوله للصفحة المناسبة
      if (token) {
        try {
          let redirectPath = '/';
          
          // إذا كان المستخدم من نوع admin، نحوله للوحة التحكم
          if (userType === 'admin') {
            redirectPath = '/admin/dashboard';
          } else if (userData) {
            // محاولة تحديد الصفحة المناسبة حسب دور المستخدم
            const user = JSON.parse(userData);
            if (user.roles && user.roles.length > 0) {
              const mainRole = user.roles[0].name;
              if (mainRole === 'instructor') {
                redirectPath = '/instructor/dashboard';
              }
            }
          }
          
          // التحويل للصفحة المناسبة
          navigate(redirectPath);
        } catch (error) {
          console.error('خطأ في معالجة بيانات المستخدم:', error);
        }
      }
    };
    
    checkLoginStatus();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // الحصول على البصمة الموسعة للجهاز
      const enhancedFingerprint = await fingerprintService.getEnhancedFingerprint();
      const deviceInfo = await deviceMatchingService.getDeviceInfo();
      
      // تحليل معلومات الجهاز بشكل أكثر تفصيلاً
      const detailedDeviceInfo = await deviceMatchingService.analyzeDevice();
      
      // دمج معلومات الجهاز المفصلة مع البصمة الموسعة
      const combinedFingerprint = {
        ...enhancedFingerprint,
        deviceInfo: detailedDeviceInfo
      };
      
      // إعداد معلومات الجهاز للتأكد من أن جميع الحقول هي نصوص
      const formattedDeviceInfo = {
        type: deviceInfo.type || 'unknown',
        os: typeof deviceInfo.os === 'string' ? deviceInfo.os : (deviceInfo.os?.name || 'unknown'),
        browser: typeof deviceInfo.browser === 'string' ? deviceInfo.browser : (deviceInfo.browser?.name || 'unknown'),
        region: deviceInfo.region || ''
      };
      
      // معلومات الجهاز المنسقة
      
      // إرسال بيانات تسجيل الدخول مع البصمة الموسعة
      const response = await axiosInstance.post('/api/login', {
        identifier: phone,
        password,
        fingerprint: enhancedFingerprint.fingerprint,
        enhanced_fingerprint: JSON.stringify(combinedFingerprint),
        device_info: formattedDeviceInfo
      });
      if (response.data.token) {
        sessionStorage.setItem('token', response.data.token);
        sessionStorage.setItem('user', JSON.stringify(response.data.user));
        
        // حفظ معلومات الدور (هذه ضرورية للصلاحيات)
        if (response.data.user && response.data.user.role_id) {
          sessionStorage.setItem('userRoleId', response.data.user.role_id);
        }
        
        // إظهار رسالة تحميل - نستخدم رسالة عامة لأسباب أمنية
        const loadingToast = toastService.loading('جاري تسجيل الدخول...');

        // تأخير التوجيه للسماح بتحميل الصلاحيات
        setTimeout(() => {
          // محاولة مزامنة الصلاحيات
          const { syncUserPermissions } = require('../../services/permissionsSyncService');
          try {
            syncUserPermissions();
          } catch (error) {
            console.error('خطأ في تحميل الصلاحيات:', error);
          }
          
          // إغلاق رسالة التحميل
          toastService.update(loadingToast, 'تم تسجيل الدخول بنجاح', 'success');
          
          // التوجيه للصفحة المطلوبة
          const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
          if (redirectUrl) {
            sessionStorage.removeItem('redirectAfterLogin');
            navigate(redirectUrl);
          } else {
            const userRoles = response.data.user.roles || [];
            let redirectPath = '/';

            if (userRoles.length > 0) {
              const mainRole = userRoles[0].name;
              switch(mainRole) {
                case 'instructor':
                  redirectPath = '/instructor/dashboard';
                  break;
                case 'student':
                  redirectPath = sessionStorage.getItem('prevPath') || '/';
                  sessionStorage.removeItem('prevPath');
                  break;
              }
            }
            navigate(redirectPath);
          }
        }, 1000); // تأخير 1 ثانية للسماح بتحميل الصلاحيات
      }
    } catch (err) {
      console.error('Login Error:', err);
      const errorMessage = err.response?.data?.message || 'حدث خطأ في تسجيل الدخول';
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (email) => {
    if (email) {
      const loadingToast = toastService.loading('جاري إرسال رابط إعادة تعيين كلمة المرور...');
      try {
        await axiosInstance.post('/api/forgot-password', { email });
        toastService.update(
          loadingToast,
          'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني',
          'success'
        );
        setShowForgotPassword(false);
      } catch (err) {
        console.error('Forgot Password Error:', err);
        toastService.update(
          loadingToast,
          'حدث خطأ في إرسال رابط إعادة تعيين كلمة المرور',
          'error'
        );
      }
    }
  };

  return (
    <div className="h-screen grid place-items-center overflow-hidden bg-white dark:bg-gray-900">
      {/* Background Blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none section-bg">
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-purple-500/20 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ scale: 0.9, opacity: 0.5 }}
          animate={{ 
            scale: [0.9, 1.1, 0.9],
            opacity: [0.5, 0.7, 0.5]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-purple-500/10 dark:bg-blue-500/20 rounded-full blur-3xl"
        />
      </div>

      <div className="w-full max-w-md px-4 sm:px-6 lg:px-8">
        <AnimatePresence mode="wait">
          {!showForgotPassword ? (
            <motion.div
              key="login"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-[0.5rem] [border-top-right-radius:3rem] [border-bottom-left-radius:3rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] dark:shadow-2xl border border-gray-300 dark:border-gray-700"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  تسجيل الدخول
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  أهلاً بك مرة أخرى
                </p>
              </div>

              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div className="relative">
                    <input
                      id="phone"
                      name="phone"
                      type="text"
                      required
                      className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                      placeholder="أدخل رقم الجوال"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      dir="rtl"
                    />
                    <label 
                      htmlFor="phone" 
                      className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                    >
                      رقم الجوال
                    </label>
                  </div>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                      placeholder="أدخل كلمة المرور"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      dir="rtl"
                    />
                    <label 
                      htmlFor="password" 
                      className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                    >
                      كلمة المرور
                    </label>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    نسيت كلمة المرور؟
                  </button>
                  <Link
                    to="/register"
                    className="text-sm font-medium text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300"
                  >
                    تسجيل حساب جديد
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="group relative w-full flex justify-center py-2 sm:py-2 py-3 px-6 border border-transparent text-sm font-medium rounded-full text-white bg-blue-500 hover:bg-blue-600 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  {loading ? (
                    <div className="w-4 sm:w-4 w-5 h-4 sm:h-4 h-5 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    "تسجيل الدخول"
                  )}
                </button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="forgot-password"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl p-8 rounded-[0.5rem] [border-top-right-radius:3rem] [border-bottom-left-radius:3rem] shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)] dark:shadow-2xl border border-gray-300 dark:border-gray-700"
            >
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  نسيت كلمة المرور
                </h2>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  سنرسل لك رابط لإعادة تعيين كلمة المرور
                </p>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                handleForgotPassword(e.target.email.value);
              }} className="space-y-6">
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    className="peer h-10 max-sm:h-12 w-full px-3 text-sm rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent align-middle"
                    placeholder="أدخل بريدك الإلكتروني"
                    dir="rtl"
                  />
                  <label 
                    htmlFor="email" 
                    className="absolute right-3 -top-2.5 bg-gray-50 dark:bg-gray-700 px-1 text-xs font-medium text-gray-700 dark:text-gray-300 transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-500 peer-placeholder-shown:dark:text-gray-400 peer-placeholder-shown:top-3 peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-gray-700 peer-focus:dark:text-gray-300 flex items-center"
                  >
                    البريد الإلكتروني
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    className="flex-1 flex justify-center py-2 sm:py-2 py-3 px-6 text-sm font-medium rounded-full text-white bg-blue-500 hover:bg-blue-600 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    إرسال رابط التعيين
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(false)}
                    className="flex-1 flex justify-center py-2 sm:py-2 py-3 px-6 text-sm font-medium rounded-full text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                  >
                    رجوع
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Login;
